/**
 * DupSource / SpikeSource selection-combo candidates — the ONE implementation shared
 * by the QC Batch and Sample Prep Batch sequence grids.
 *
 * Ports legacy ALPACA's per-row option map, which both controllers build with
 * byte-identical logic under different names:
 *   • QC   — `QCBatchController.BuildSourceOptionsJson`            (:8027)
 *   • Prep — `SamplePreparationController.BuildSamplePrepSourceOptionsJson` (:3455)
 * (An earlier note claiming the prep form lacked this feature was wrong — it greps
 * clean only because prep uses prep-prefixed helper names.)
 *
 * WHAT THE COMBO IS.  Both `QCBatchSequence` and `SamplePrepBatchSequence` COMPUTE
 * DupSource/SpikeSource in their getters, but only while the backing field is null
 * (`if (_DupSource == null) …`).  So the dropdown is an analyst **override** layered on
 * the computed default — not the source of the value.  AI already reproduces the
 * computed half in `QcSequenceBuilder.ApplyDupSpikeSource`; this is the override half.
 *
 * THE RULE (identical in both legacy controllers):
 *   • SpikeSource — offered only when the row's QC type has root role "Spike"
 *     (MS / MSD / LFSM…).  Candidates = SYSSamplecode of every **Sample**-type row
 *     belonging to the SAME sample as this row.
 *   • DupSource   — offered only when the row's QC role is "Duplicate" AND its type
 *     declares a QCSource.  Candidates = rows whose QCTypeName equals that QCSource,
 *     excluding the row itself; and when the source is sample-bound
 *     ("Sample" | "MS" | "LFSM_L" | "LFSM_M" | "LFSM_H") also restricted to the same sample.
 *   • "Same sample" = same SampleLogIn; falls back to equal StrSampleId when the
 *     reference is missing.
 *   • Distinct, non-empty codes only.  A row with BOTH lists empty gets NO combo —
 *     legacy cancels the editor outright (`e.cancel = true`), so the cell is plain text.
 */

/** The sequence-row fields this needs — structurally satisfied by BOTH forms' row types. */
export interface SourceCandidateRow {
  id: string;
  qc: string;                     // QCTypeName of THIS row ("Sample", "Dup", "MS", …)
  qcTypeOid?: string | null;
  code: string;                   // SYSSamplecode — what the combo actually stores
  sampleLogInOid?: string | null;
  strSampleId?: string;
}

/** The QC-type metadata this needs (subset of the forms' QcTypeOption). */
export interface SourceCandidateType {
  qcTypeOid: string;
  qcTypeName?: string | null;
  qcSource?: string | null;
  qcRole?: string | null;
  qcRootRole?: string | null;
}

export interface SourceCandidates { dup: string[]; spike: string[] }

/** Legacy's sample-bound source set — these narrow the Duplicate candidates to the
 *  row's own sample (QCBatchController :8062 / SamplePreparationController :3490). */
const SAMPLE_BOUND_SOURCES = new Set(['Sample', 'MS', 'LFSM_L', 'LFSM_M', 'LFSM_H']);

const eq = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * Per-row candidate lists, keyed by row id.  Rows with no candidates are ABSENT from
 * the map — callers render those cells as plain text (legacy's non-editable cell).
 */
export function buildSourceOptions(
  rows: readonly SourceCandidateRow[],
  qcTypes: readonly SourceCandidateType[],
): Map<string, SourceCandidates> {
  const typeByOid = new Map(qcTypes.map(t => [t.qcTypeOid, t]));
  const out = new Map<string, SourceCandidates>();

  // Two rows are the same sample when they share a SampleLogIn; StrSampleId is the
  // documented fallback for rows whose reference is missing.
  const sameSample = (a: SourceCandidateRow, b: SourceCandidateRow) => {
    if (a.sampleLogInOid && b.sampleLogInOid) return a.sampleLogInOid === b.sampleLogInOid;
    return !!a.strSampleId && a.strSampleId === b.strSampleId;
  };

  const distinct = (list: SourceCandidateRow[]) => {
    const seen = new Set<string>();
    for (const r of list) if (r.code) seen.add(r.code);
    return [...seen];
  };

  for (const row of rows) {
    const type = row.qcTypeOid ? typeByOid.get(row.qcTypeOid) : undefined;
    if (!type) continue;                                  // legacy: `row.QCType == null` → skip

    const spike = eq(type.qcRootRole, 'Spike')
      ? distinct(rows.filter(a => eq(a.qc, 'Sample') && sameSample(a, row)))
      : [];

    let dup: string[] = [];
    if (eq(type.qcRole, 'Duplicate') && (type.qcSource ?? '').trim() !== '') {
      const src = (type.qcSource ?? '').trim();
      let candidates = rows.filter(a => eq(a.qc, src) && a.id !== row.id);
      if (SAMPLE_BOUND_SOURCES.has(src)) candidates = candidates.filter(a => sameSample(a, row));
      dup = distinct(candidates);
    }

    if (spike.length === 0 && dup.length === 0) continue;  // no combo for this row
    out.set(row.id, { dup, spike });
  }
  return out;
}

/** A correction to apply to one row; only the fields present need writing. */
export interface SourceFix { id: string; dupSource?: string; spikeSource?: string }

/**
 * RECONCILE stored Dup/Spike sources against the CURRENT candidates.
 *
 * Two situations, one rule.  A stored value that is not among the row's candidates is
 * invalid — most often because the row it pointed at was REMOVED (user report
 * 2026-08-07: added a dilution, pointed DupSource at it, then removed the dilution).
 * When that happens we do NOT simply blank the cell:
 *
 *   • exactly ONE candidate remains  → adopt it.  This is the computed default, and it
 *     is what legacy shows too — its BO getters recompute whenever the stored field is
 *     null, so a cleared value there immediately falls back to the default.  Blanking
 *     instead created a DEAD END in AI, because a single-candidate cell renders as
 *     text (no dropdown), leaving the analyst with an empty cell and no way to set it.
 *   • two or more candidates        → clear, and the cell renders its dropdown so the
 *     analyst re-picks deliberately (we must not guess between real alternatives).
 *
 * The same rule fills a legitimately-empty cell that has exactly one candidate, so a
 * Dup/Spike row can never sit blank while a single valid source exists.
 *
 * Rows with NO candidates are left alone unless their stored value is dangling (points
 * at a code no row carries any more), in which case it is cleared.
 */
export function reconcileSources(
  rows: readonly (SourceCandidateRow & { dupSource?: string | null; spikeSource?: string | null })[],
  opts: Map<string, SourceCandidates>,
): SourceFix[] {
  const live = new Set(rows.map(r => r.code).filter(Boolean));
  const out: SourceFix[] = [];

  const resolve = (current: string | null | undefined, candidates: string[] | undefined): string | null => {
    const cur = (current ?? '').trim();
    if (candidates && candidates.length > 0) {
      if (candidates.includes(cur)) return null;              // already valid
      return candidates.length === 1 ? candidates[0]! : '';   // adopt the only one, else clear
    }
    return cur && !live.has(cur) ? '' : null;                 // no candidates: clear only if dangling
  };

  for (const r of rows) {
    const c = opts.get(r.id);
    const dup   = resolve(r.dupSource,   c?.dup);
    const spike = resolve(r.spikeSource, c?.spike);
    if (dup === null && spike === null) continue;
    out.push({
      id: r.id,
      ...(dup   !== null ? { dupSource:   dup   } : {}),
      ...(spike !== null ? { spikeSource: spike } : {}),
    });
  }
  return out;
}
